import { useCallback } from 'react';
import { PageSection, PageSectionVariants, TextContent, Text, Form, FormGroup, Button, Toolbar, ToolbarContent, ToolbarItem, Alert, Spinner, EmptyState, EmptyStateHeader, EmptyStateBody } from '@patternfly/react-core';
import { SaveIcon, UndoIcon } from '@patternfly/react-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
    configurationTargetSettingsSaveStarted,
    configurationTargetSettingsReverted,
    aggregateWaitTimeChanged,
    configurationTargetSettingsSelectors,
} from '../../store/configurationTargetSettings/configurationTargetSettingsSlice';
import { configurationTargetSelectors } from '../../store/configurationTarget/configurationTargetSlice';
import NumberInput, { ValidRangeDisplay } from '../../components/number-input';

const ConfigurationTargetSettingsPage = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const currentTarget = useSelector(configurationTargetSelectors.selectCurrent);
    const aggregateWaitTime = useSelector(configurationTargetSettingsSelectors.selectAggregateWaitTime);
    const isLoading = useSelector(configurationTargetSettingsSelectors.selectIsLoading);
    const isSaving = useSelector(configurationTargetSettingsSelectors.selectIsSaving);
    const fetchError = useSelector(configurationTargetSettingsSelectors.selectFetchError);
    const saveError = useSelector(configurationTargetSettingsSelectors.selectSaveError);
    const hasUnsavedChanges = useSelector(configurationTargetSettingsSelectors.selectHasUnsavedChanges);

    const handleAggregateWaitTimeChanged = useCallback((value: number) => {
        dispatch(aggregateWaitTimeChanged(value));
    }, [dispatch]);

    const handleAggregateWaitTimeCleared = useCallback(() => {
        dispatch(aggregateWaitTimeChanged(null));
    }, [dispatch]);

    const handleSave = useCallback(() => {
        dispatch(configurationTargetSettingsSaveStarted());
    }, [dispatch]);

    const handleUndo = useCallback(() => {
        dispatch(configurationTargetSettingsReverted());
    }, [dispatch]);

    if (currentTarget === undefined) {
        return (
            <PageSection variant={PageSectionVariants.light}>
                <EmptyState>
                    <EmptyStateHeader titleText={<>{t('selectConfigurationTarget.title')}</>} headingLevel="h4" />
                    <EmptyStateBody>{t('selectConfigurationTarget.message')}</EmptyStateBody>
                </EmptyState>
            </PageSection>
        );
    }

    return (
        <>
            <PageSection variant={PageSectionVariants.default}>
                <TextContent>
                    <Text component="h1">{t('page.configurationTargetSettings.title')}</Text>
                </TextContent>
            </PageSection>

            <PageSection variant={PageSectionVariants.light}>
                {fetchError && (
                    <Alert variant="danger" isInline title={fetchError} className="pf-v5-u-mb-md" />
                )}

                {saveError && (
                    <Alert variant="danger" isInline title={saveError} className="pf-v5-u-mb-md" />
                )}

                {isLoading && <Spinner size="lg" />}

                {!isLoading && !fetchError && (
                    <>
                        <Toolbar>
                            <ToolbarContent>
                                <ToolbarItem>
                                    <Button
                                        variant="primary"
                                        isDisabled={!hasUnsavedChanges || isSaving}
                                        onClick={handleSave}
                                    >
                                        {isSaving ? <Spinner size="md" /> : <SaveIcon />}
                                        {' '}{t('common.save')}
                                    </Button>
                                </ToolbarItem>
                                <ToolbarItem>
                                    <Button
                                        variant="secondary"
                                        isDisabled={!hasUnsavedChanges || isSaving}
                                        onClick={handleUndo}
                                    >
                                        <UndoIcon />
                                        {' '}{t('common.undo')}
                                    </Button>
                                </ToolbarItem>
                            </ToolbarContent>
                        </Toolbar>

                        <Form>
                            <FormGroup
                                label={t('page.configurationTargetSettings.aggregateWaitTime.label')}
                                fieldId="aggregate-wait-time"
                            >
                                <div style={{ maxWidth: '20rem' }}>
                                    <NumberInput
                                        fieldId="aggregate-wait-time"
                                        defaultValue={aggregateWaitTime ?? undefined}
                                        onChange={handleAggregateWaitTimeChanged}
                                        allowEmpty
                                        onEmpty={handleAggregateWaitTimeCleared}
                                        showClearButton
                                        showSpinnerButtons
                                        step={10}
                                        lowerBound={0}
                                        upperBound={9000}
                                        validRangeDisplay={ValidRangeDisplay.OnInteraction}
                                    />
                                </div>
                            </FormGroup>
                        </Form>
                    </>
                )}
            </PageSection>
        </>
    );
};

export default ConfigurationTargetSettingsPage;
